// src/components/multiplayer/HostLobbyPanel.tsx
import React, { useState } from "react";
import "./HostLobbyPanel.css";

interface OpponentStats {
  rank: number;
  rating: number;
  gamesPlayed: number;
  winsPercentage: number;
}

interface HostLobbyPanelProps {
  isHosting: boolean;
  roomId: string | null;
  gameVariant?: string;
  opponentJoined: boolean;
  opponentUsername: string | null;
  opponentStats?: OpponentStats | null;
  onStartGame: () => void;
  onCancelHosting: () => void;
}

export const HostLobbyPanel: React.FC<HostLobbyPanelProps> = ({
  isHosting,
  roomId,
  gameVariant,
  opponentJoined,
  opponentUsername,
  opponentStats,
  onStartGame,
  onCancelHosting,
}) => {
  const [copied, setCopied] = useState(false);

  // Handle copy room code
  const handleCopy = async () => {
    if (roomId) {
      try {
        await navigator.clipboard.writeText(roomId);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("Failed to copy:", err);
      }
    }
  };

  // Not hosting state
  if (!isHosting) {
    return (
      <div className="host-lobby-container">
        <div className="host-lobby-empty">
          <span className="empty-icon">🎯</span>
          <p className="empty-text">No Active Lobby</p>
          <p className="empty-subtext">
            Create a game in the <strong>Create Game</strong> panel to host a lobby
          </p>
        </div>
      </div>
    );
  }

  // Hosting state
  return (
    <div className="host-lobby-container">
      {/* Room Code Section - Compact */}
      <div className="room-code-section">
        <label className="room-code-label">Room Code</label>
        <div className="room-code-display">
          <span className="room-code-text">{roomId}</span>
          <button
            className={`copy-btn ${copied ? "copied" : ""}`}
            onClick={handleCopy}
            title="Copy room code"
          >
            {copied ? "✓" : "📋"}
          </button>
        </div>
        <p className="room-code-hint">Share this code with your opponent</p>
      </div>

      {/* Opponent Status Section - Compact */}
      <div className="opponent-status-section">
        <div className="status-header">
          <span className="status-icon">
            {opponentJoined ? "✓" : "⏳"}
          </span>
          <span className="status-text">
            {opponentJoined ? "Opponent Joined" : "Waiting for Opponent"}
          </span>
        </div>
        {opponentJoined && opponentUsername && (
          <div className="opponent-info">
            <div className="opponent-name-row">
              <span className="opponent-label">Player:</span>
              <span className="opponent-name">{opponentUsername}</span>
            </div>
            {opponentStats && (
              <div className="opponent-stats">
                <div className="stat-item">
                  <span className="stat-label">Rank:</span>
                  <span className="stat-value">{opponentStats.rank || 'N/A'}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Rating:</span>
                  <span className="stat-value">{opponentStats.rating || 'N/A'}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Games:</span>
                  <span className="stat-value">{opponentStats.gamesPlayed}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-label">Wins:</span>
                  <span className="stat-value">{opponentStats.winsPercentage.toFixed(1)}%</span>
                </div>
              </div>
            )}
          </div>
        )}
        {!opponentJoined && (
          <p className="status-subtext">Your opponent will appear here once they join</p>
        )}
      </div>

      {/* Action Buttons - Compact */}
      <div className="host-actions">
        <button
          className="start-game-btn"
          onClick={onStartGame}
          disabled={!opponentJoined}
        >
          <span className="btn-icon">▶</span>
          <span className="btn-text">Start Game</span>
        </button>
        <button
          className="cancel-hosting-btn"
          onClick={onCancelHosting}
        >
          <span className="btn-icon">✕</span>
          <span className="btn-text">Cancel</span>
        </button>
      </div>
    </div>
  );
};

export default HostLobbyPanel;
